import { createStore, useStore } from "zustand";

/**
 * Shell chrome UI state — e.g. whether the app's nav rail is collapsed.
 *
 * This is deliberately narrow: shell/chrome concerns only. It has zero
 * knowledge of routing, auth, or business/server data — those belong to
 * the consuming app (routing/auth) or to React Query (server data), not
 * here.
 */
export type ShellState = {
  /** Whether the nav rail is collapsed to its icon-only width. */
  navRailCollapsed: boolean;
  /** Flip `navRailCollapsed`. */
  toggleNavRail: () => void;
  /** Set `navRailCollapsed` to an explicit value. */
  setNavRailCollapsed: (collapsed: boolean) => void;
};

export type ShellStoreOptions = {
  /**
   * Initial value for `navRailCollapsed`. Defaults to `true` (collapsed,
   * icon-only) — matching nazare-frontend's existing production rail, which
   * is always the narrow 64px icon rail, not an expanded-with-labels state.
   */
  initialNavRailCollapsed?: boolean;
};

/**
 * Creates a standalone shell-state store.
 *
 * Most consumers should just use the shared {@link useShellStore} hook
 * below, which reads/writes a single app-wide instance. Reach for this
 * factory directly when you need an isolated instance instead — e.g. in
 * tests, or to scope shell state below the whole app.
 */
export function createShellStore(options: ShellStoreOptions = {}) {
  const { initialNavRailCollapsed = true } = options;

  return createStore<ShellState>((set) => ({
    navRailCollapsed: initialNavRailCollapsed,
    toggleNavRail: () =>
      set((state) => ({ navRailCollapsed: !state.navRailCollapsed })),
    setNavRailCollapsed: (collapsed) => set({ navRailCollapsed: collapsed }),
  }));
}

export type ShellStoreApi = ReturnType<typeof createShellStore>;

const NAV_RAIL_COLLAPSED_STORAGE_KEY = "nazare-ui:nav-rail-collapsed";

function readPersistedNavRailCollapsed(): boolean | undefined {
  if (typeof window === "undefined") return undefined;
  const raw = window.localStorage.getItem(NAV_RAIL_COLLAPSED_STORAGE_KEY);
  if (raw === "true") return true;
  if (raw === "false") return false;
  return undefined;
}

// Shared, app-wide instance backing `useShellStore`. Shell chrome state
// (like "is the nav rail collapsed") is app-level singleton UI state, so a
// single default store is the right default for the common case. Only this
// shared instance persists to localStorage — `createShellStore()` called
// directly (tests, a scoped instance) stays in-memory only.
const shellStore = createShellStore({
  initialNavRailCollapsed: readPersistedNavRailCollapsed(),
});

if (typeof window !== "undefined") {
  shellStore.subscribe((state, prevState) => {
    if (state.navRailCollapsed === prevState.navRailCollapsed) return;
    window.localStorage.setItem(
      NAV_RAIL_COLLAPSED_STORAGE_KEY,
      String(state.navRailCollapsed),
    );
  });
}

/**
 * Hook onto the shared shell-state store. Call with no arguments to read
 * the whole state, or pass a selector to subscribe to just the slice you
 * need (the usual zustand selector pattern — it avoids re-rendering on
 * unrelated shell-state changes).
 *
 * @example
 * const navRailCollapsed = useShellStore((s) => s.navRailCollapsed);
 * const toggleNavRail = useShellStore((s) => s.toggleNavRail);
 */
export function useShellStore<T = ShellState>(
  selector: (state: ShellState) => T = (state) => state as unknown as T,
): T {
  return useStore(shellStore, selector);
}
